import { lazy, Suspense } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./hooks/useAuth";
import { EmpresaProvider, useEmpresaAtiva } from "./hooks/useEmpresaAtiva";
import { VerComoProvider, useVerComo } from "./hooks/useVerComo";
// Layout e RecoveryGate ficam EAGER — aparecem em toda tela logada, não vale
// separar em chunk. As páginas abaixo são carregadas sob demanda (code-splitting):
// cada uma vira um chunk próprio, então o primeiro acesso baixa só o essencial em
// vez do bundle inteiro. Todas usam named export → mapeamos pra { default }.
import { Layout } from "./components/Layout"; import { AvisoListaCortada } from "./components/AvisoListaCortada"; // "total nunca parcial"
import { RecoveryGate } from "./components/RecoveryGate"; import { PortaoDuasEtapas, AvisoDuasEtapas } from "./components/PortaoDuasEtapas"; /* mock 11 (14/10): o login pede o código a quem tem o 2º passo */
import { ChunkErrorBoundary } from "./components/ChunkErrorBoundary";
import { paginaPublicaDaUrl } from "./rotas/paginaPublica";
import { CarregandoSessao } from "./components/CarregandoSessao";
import { capturarMarcaDaUrl } from "./lib/marcaLogin";

// ?marca= chega nos links de convite apontando pra "/" — os <Navigate> abaixo
// descartam a query, então a dica da marca do login é persistida já na entrada.
capturarMarcaDaUrl();

const LoginPage = lazy(() => import("./pages/Login").then((m) => ({ default: m.LoginPage })));
import { EmpresaIndisponivel } from "./components/EmpresaIndisponivel";
import { skinAtiva } from "./lib/skin";
const DashboardPage = lazy(() => import("./pages/Dashboard").then((m) => ({ default: m.DashboardPage }))); const PrecisaDeVocePage = lazy(() => import("./pages/PrecisaDeVoce").then((m) => ({ default: m.PrecisaDeVocePage })));
const OQueMudouPage = lazy(() => import("./pages/portal/OQueMudou").then((m) => ({ default: m.OQueMudouPage }))); // o que mudou para você (mock 05, 06/09)
const PainelV2Page = lazy(() => import("./pages/PainelV2").then((m) => ({ default: m.PainelV2Page })));
import { OperadorPainelPage, OperadorClientesPage, OperadorAuditoriasPage, OperadorSegurancaPage, OperadorTelemetriaPage, OperadorPareceresPage, OperadorCanaisPage, OperadorConferenciaFolhaPage, BaseComparativaPage } from "./rotas/paginasDoOperador"; import { BilheteDoPdf } from "./components/BilheteDoPdf"; /* 48 §4: o bilhete do PDF fora do useAuth (mesma linha: arquivo cravado no portão) */
const ManualDeBordoPage = lazy(() => import("./pages/ManualDeBordo").then((m) => ({ default: m.ManualDeBordoPage })));
const ClientesPage = lazy(() => import("./pages/Clientes").then((m) => ({ default: m.ClientesPage })));
const MapaCarteiraPage = lazy(() => import("./pages/MapaCarteira").then((m) => ({ default: m.MapaCarteiraPage })));
const PipelinePage = lazy(() => import("./pages/Pipeline").then((m) => ({ default: m.PipelinePage }))); const ProspeccaoPage = lazy(() => import("./pages/Prospeccao").then((m) => ({ default: m.ProspeccaoPage }))); // Prospecção: mock aprovado 12/09
const ObrasPage = lazy(() => import("./pages/Obras").then((m) => ({ default: m.ObrasPage })));
const ObrasLocalizacaoPage = lazy(() => import("./pages/ObrasLocalizacao").then((m) => ({ default: m.ObrasLocalizacaoPage })));
const OrcamentosPage = lazy(() => import("./pages/Orcamentos").then((m) => ({ default: m.OrcamentosPage })));
const ContratosPage = lazy(() => import("./pages/Contratos").then((m) => ({ default: m.ContratosPage })));
const MedicoesPage = lazy(() => import("./pages/Medicoes").then((m) => ({ default: m.MedicoesPage })));
const SaudeManutencaoPage = lazy(() => import("./pages/SaudeManutencao").then((m) => ({ default: m.SaudeManutencaoPage })));
const AtivosPage = lazy(() => import("./pages/Ativos").then((m) => ({ default: m.AtivosPage })));
const AcessoPage = lazy(() => import("./pages/Acesso").then((m) => ({ default: m.AcessoPage })));
const ColaboradoresPage = lazy(() => import("./pages/equipe/ColaboradoresRouter").then((m) => ({ default: m.ColaboradoresRouter })));
const PortalPage = lazy(() => import("./pages/Portal").then((m) => ({ default: m.PortalPage })));
const MeusChamadosPage = lazy(() => import("./pages/MeusChamados").then((m) => ({ default: m.MeusChamadosPage })));
const FiscalV2Page = lazy(() => import("./pages/FiscalV2").then((m) => ({ default: m.FiscalV2Page })));
const PainelDinheiroV2Page = lazy(() => import("./pages/PainelDinheiroV2").then((m) => ({ default: m.PainelDinheiroV2Page })));
const ConciliacaoListaPage = lazy(() => import("./pages/ConciliacaoLista").then((m) => ({ default: m.ConciliacaoListaPage }))); // a Conciliação em uma lista (mock 11/09) — rota própria até o aceite do dono
const MensagensPage = lazy(() => import("./pages/Mensagens").then((m) => ({ default: m.MensagensPage })));
const ChecklistsPage = lazy(() => import("./pages/Checklists").then((m) => ({ default: m.ChecklistsPage })));
const RHPage = lazy(() => import("./pages/RH").then((m) => ({ default: m.RHPage })));
const BISitePage = lazy(() => import("./pages/BISite").then((m) => ({ default: m.BISitePage })));
const ContasReceberV2Page = lazy(() => import("./pages/ContasReceberV2").then((m) => ({ default: m.ContasReceberV2Page })));
const ContasPagarV2Page = lazy(() => import("./pages/ContasPagarV2").then((m) => ({ default: m.ContasPagarV2Page })));
const MandaDocumentoPage = lazy(() => import("./pages/MandaDocumento").then((m) => ({ default: m.MandaDocumentoPage })));
const ObraDetalhePage = lazy(() => import("./pages/ObraDetalhe").then((m) => ({ default: m.ObraDetalhePage })));
const ObraDetalheV2Page = lazy(() => import("./pages/obras/ObraDetalheV2").then((m) => ({ default: m.ObraDetalheV2Page })));
const ObrasV2Page = lazy(() => import("./pages/obras/ObrasV2").then((m) => ({ default: m.ObrasV2Page })));
const CronogramaPage = lazy(() => import("./pages/Cronograma").then((m) => ({ default: m.CronogramaPage })));
const ComunicacaoPage = lazy(() => import("./pages/Comunicacao").then((m) => ({ default: m.ComunicacaoPage })));
const WhatsappPage = lazy(() => import("./pages/Whatsapp").then((m) => ({ default: m.WhatsappPage })));
const SociosV2Page = lazy(() => import("./pages/SociosV2").then((m) => ({ default: m.SociosV2Page })));
const LivroSociosV2Page = lazy(() => import("./pages/SociosV2").then((m) => ({ default: m.LivroSociosV2Page })));
const SociosSemanaPage = lazy(() => import("./pages/SociosSemana").then((m) => ({ default: m.SociosSemanaPage })));
const UsuariosPage = lazy(() => import("./pages/equipe/UsuariosRouter").then((m) => ({ default: m.UsuariosRouter })));
const EquipePage = lazy(() => import("./pages/equipe/UsuariosRouter").then((m) => ({ default: m.EquipeRouter })));
const UsuariosPlataformaPage = lazy(() => import("./pages/equipe/UsuariosRouter").then((m) => ({ default: m.UsuariosPlataformaRouter })));
const EngenhariaPage = lazy(() => import("./pages/Engenharia").then((m) => ({ default: m.EngenhariaPage })));
const CatalogoServicosPage = lazy(() => import("./pages/CatalogoServicos").then((m) => ({ default: m.CatalogoServicosPage })));
const PrestadoresPage = lazy(() => import("./pages/Prestadores").then((m) => ({ default: m.PrestadoresPage })));
const PlanoAcoesPage = lazy(() => import("./pages/PlanoAcoes").then((m) => ({ default: m.PlanoAcoesPage })));
const LedgerPage = lazy(() => import("./pages/Ledger").then((m) => ({ default: m.LedgerPage })));
const VencimentosPage = lazy(() => import("./pages/Vencimentos").then((m) => ({ default: m.VencimentosPage })));
const FechamentoV2Page = lazy(() => import("./pages/FechamentoV2").then((m) => ({ default: m.FechamentoV2Page })));
const RazaoV2Page = lazy(() => import("./pages/RazaoV2").then((m) => ({ default: m.RazaoV2Page })));
const FolhaPage = lazy(() => import("./pages/equipe/FolhaRouter").then((m) => ({ default: m.FolhaRouter })));
const TributarioPage = lazy(() => import("./pages/Tributario").then((m) => ({ default: m.TributarioPage })));
const SobrePage = lazy(() => import("./pages/Sobre").then((m) => ({ default: m.SobrePage }))); const DuasEtapasPage = lazy(() => import("./pages/DuasEtapas").then((m) => ({ default: m.DuasEtapasPage }))); const ApiIntegracoesRegulaPage = lazy(() => import("./pages/ApiIntegracoesRegula").then((m) => ({ default: m.ApiIntegracoesRegulaPage }))); // D12 (11/10): API e integrações da Regula (mesma linha: arquivo cravado)
const AuditoriaPage = lazy(() => import("./pages/Auditoria").then((m) => ({ default: m.AuditoriaPage })));
const NotificacoesPage = lazy(() => import("./pages/Notificacoes").then((m) => ({ default: m.NotificacoesPage })));
const CentrosPage = lazy(() => import("./pages/Centros").then((m) => ({ default: m.CentrosPage })));
const CentroDetalhePage = lazy(() => import("./pages/CentroDetalhe").then((m) => ({ default: m.CentroDetalhePage })));
const AchadosPage = lazy(() => import("./pages/Achados").then((m) => ({ default: m.AchadosPage }))); const RiscoDeInterdicaoPage = lazy(() => import("./pages/RiscoDeInterdicao").then((m) => ({ default: m.RiscoDeInterdicaoPage }))); const RadarDaReformaPage = lazy(() => import("./pages/RadarDaReforma").then((m) => ({ default: m.RadarDaReformaPage }))); const AutosDeInfracaoPage = lazy(() => import("./pages/AutosDeInfracao").then((m) => ({ default: m.AutosDeInfracaoPage }))); const AuditoriaDoContratoPage = lazy(() => import("./pages/AuditoriaDoContrato").then((m) => ({ default: m.AuditoriaDoContratoPage }))); const CalendarioDeRenovacoesPage = lazy(() => import("./pages/CalendarioDeRenovacoes").then((m) => ({ default: m.CalendarioDeRenovacoesPage }))); const RenovarOuNaoPage = lazy(() => import("./pages/RenovarOuNao").then((m) => ({ default: m.RenovarOuNaoPage }))); const EditorDeRoteirosPage = lazy(() => import("./pages/EditorDeRoteiros").then((m) => ({ default: m.EditorDeRoteirosPage }))); const CarteiraSeguradoraRegulaPage = lazy(() => import("./pages/CarteiraSeguradoraRegula").then((m) => ({ default: m.CarteiraSeguradoraRegulaPage }))); const EsgRegulaPage = lazy(() => import("./pages/EsgRegula").then((m) => ({ default: m.EsgRegulaPage }))); /* mock 45 seção 2 (14/10): ESG da carteira */ // mock 03 do pacote 2 (14/10) · D5 (mock 13, 10/10) · D8 (mock 14, 11/10) · D10 (mock 16, 11/10) · D9 (mock 15, 11/10)
const FontesAuditoriaPage = lazy(() => import("./pages/FontesAuditoria").then((m) => ({ default: m.FontesAuditoriaPage })));
const RequisitosConformidadePage = lazy(() => import("./pages/RequisitosConformidade").then((m) => ({ default: m.RequisitosConformidadePage })));
const MapaCoberturaPage = lazy(() => import("./pages/MapaCobertura").then((m) => ({ default: m.MapaCoberturaPage })));
const FaturamentoRegulaPage = lazy(() => import("./pages/FaturamentoRegula").then((m) => ({ default: m.FaturamentoRegulaPage }))); const LinkedInRegulaPage = lazy(() => import("./pages/LinkedInRegula").then((m) => ({ default: m.LinkedInRegulaPage }))); // LinkedIn da Regula (mock 12, 10/10)
const OnboardingRegulaPage = lazy(() => import("./pages/OnboardingRegula").then((m) => ({ default: m.OnboardingRegulaPage })));
const BibliotecaPage = lazy(() => import("./pages/Biblioteca").then((m) => ({ default: m.BibliotecaPage })));
const Repositorio = lazy(() => import("./pages/Repositorio").then((m) => ({ default: m.Repositorio })));
const CustoPorCliente = lazy(() => import("./pages/CustoPorCliente").then((m) => ({ default: m.CustoPorCliente })));
const HomeDoClientePage = lazy(() => import("./pages/portal/HomeDoCliente").then((m) => ({ default: m.HomeDoClientePage }))); // Portal v3: substituiu PortalRegulaPage; as DUAS portas da home (switcher de /portal e o deep-link /portal-regula) rendem este MESMO componente
const PortalRegulaCentroPage = lazy(() => import("./pages/PortalRegula").then((m) => ({ default: m.PortalRegulaCentroPage })));
const PortalEconomiaPage = lazy(() => import("./pages/portal/EconomiaDoCliente").then((m) => ({ default: m.EconomiaDoClientePage })));
const PortalVencimentosPage = lazy(() => import("./pages/PortalVencimentos").then((m) => ({ default: m.PortalVencimentosPage })));
const PortalBoletosPage = lazy(() => import("./pages/PortalRegula").then((m) => ({ default: m.PortalBoletosPage }))); const CentralBoletosPage = lazy(() => import("./pages/CentralBoletosPage").then((m) => ({ default: m.CentralBoletosPage })));
const CompararUnidadesPage = lazy(() => import("./pages/portal/CompararUnidades").then((m) => ({ default: m.CompararUnidadesPage }))); const PiramideDoRateioPage = lazy(() => import("./pages/portal/PiramideDoRateioPage").then((m) => ({ default: m.PiramideDoRateioPage }))); /* mock 09 do pacote 2 (14/10): a pirâmide do rateio */ const PainelDoBoardPage = lazy(() => import("./pages/portal/PainelDoBoardPage").then((m) => ({ default: m.PainelDoBoardPage }))); /* mock 13 do pacote 2 (14/10): o painel do board */
const RenovacoesPage = lazy(() => import("./pages/portal/RenovacoesDaUnidade").then((m) => ({ default: m.RenovacoesPage }))); const ManualDoImovelPage = lazy(() => import("./pages/portal/ManualDoImovel").then((m) => ({ default: m.ManualDoImovelPage }))); // Telas 2 e 3 da renovação (mock v3, 05/09)
const PortfolioRegulaPage = lazy(() => import("./pages/PortfolioRegula").then((m) => ({ default: m.PortfolioRegulaPage })));
const MesaRevisaoRegulaPage = lazy(() => import("./pages/MesaRevisaoRegula").then((m) => ({ default: m.MesaRevisaoRegulaPage })));
const DocumentosRegulaPage = lazy(() => import("./pages/DocumentosRegula").then((m) => ({ default: m.DocumentosRegulaPage }))); const DocumentosAConfirmarPage = lazy(() => import("./pages/DocumentosAConfirmar").then((m) => ({ default: m.DocumentosAConfirmarPage }))); // 28/10: o balde "a confirmar" (abstém e confirma o tipo)
const PropostaSimuladorPage = lazy(() => import("./pages/PropostaSimulador").then((m) => ({ default: m.PropostaSimuladorPage })));

/** Fallback enquanto o chunk da página carrega. */
function PageFallback() {
  return <div className="p-8 text-muted-foreground">Carregando…</div>;
}

/**
 * Renderiza o dashboard certo conforme a empresa ativa.
 * Regula tem operação muito diferente (achados, savings, centros) — KPIs M5
 * (obras, MRR de manutenção) não fazem sentido lá.
 */
function DashboardSwitcher() {
  const { ativa, loading } = useEmpresaAtiva();
  const { user } = useAuth();
  const { alvo } = useVerComo();
  // Lente "ver como cliente": "/" mostra o PORTAL do cliente, NUNCA o painel do
  // operador. O cliente não enxerga pipeline, conversão de leads, receita/MRR/
  // success fee nem "ir pra Implantação" — isso é visão de operação.
  if (alvo?.role === "cliente") {
    // Achado #45 (auditoria 05/07): sem esperar `ativa` resolver, o primeiro
    // render pós-troca de empresa decidia com `ativa === null` (falsy em toda
    // comparação de slug) e mostrava por um instante o dashboard/portal ERRADO
    // (ex.: facilities da M5 num tenant de auditoria Regula).
    if (loading || !ativa) return <div className="p-8 text-muted-foreground">Carregando…</div>;
    return ativa?.slug === "regula360" || ativa?.slug === "vitrine-reg" ? <HomeDoClientePage /> : <PortalPage />;
  }
  // Dono da plataforma cai no Operador por padrão; lembra o último contexto
  // ('mrg-home'): se a última vez ele entrou num cliente, mantém no cliente.
  if (user?.is_owner) {
    let home: string | null = null;
    try { home = localStorage.getItem("mrg-home"); } catch { /* ignore */ }
    if (home !== "empresa") return <Navigate to="/operador" replace />;
  }
  if (loading || !ativa) return <div className="p-8 text-muted-foreground">Carregando…</div>;
  // Regula v2 — a HOME do operador é a TORRE: quem abre o sistema pergunta "como está a
  // carteira e o que precisa de mim". ⚰️ O Painel (/painel-regula) MORREU na Onda 3 — Carteira e placar Boletos/Motor moram DENTRO da Torre (lápide na lista de rotas).
  if (ativa?.slug === "regula360" || ativa?.slug === "vitrine-reg") return <PortfolioRegulaPage />;
  // Redesign v2 (Fatia 2): sob ?skin=v2, o "/" do operador M5/facilities vira o
  // Painel novo (2 heróis assimétricos). Sem o flag, a v1 (Dashboard.tsx) fica
  // byte-idêntica. Regula tem dashboard próprio e não entra aqui.
  if (skinAtiva() === "v2") return <PainelV2Page />;
  return <DashboardPage />;
}



/** Painel do dinheiro (v2-only): recebe Conciliação + Contas bancárias que saíram do
 * Fiscal na reorg "só governo". Na v1 o banco vive nas abas do Fiscal → redireciona. */
function PainelDinheiroSwitcher() {
  return skinAtiva() === "v2" ? <PainelDinheiroV2Page /> : <Navigate to="/fiscal" replace />;
}
/** Conciliação (v2-only): 👑 12/09 a porta de sempre abre a tela NOVA — o porquê está em lib/nav.ts. */
function ConciliacaoSwitcher() {
  return skinAtiva() === "v2" ? <ConciliacaoListaPage /> : <Navigate to="/fiscal" replace />;
}

/** Obras v2 (pele): sob ?skin=v2 o hub /obras e o detalhe /obras/:id viram as telas
 * novas (tri-eixo + trilho + abas). Sem o flag, a v1 (Obras/ObraDetalhe) fica
 * byte-idêntica — a rota só bifurca a renderização por pele. */
function ObrasSwitcher() {
  return skinAtiva() === "v2" ? <ObrasV2Page /> : <ObrasPage />;
}
function ObraDetalheSwitcher() {
  return skinAtiva() === "v2" ? <ObraDetalheV2Page /> : <ObraDetalhePage />;
}




/**
 * "Meu Painel" do cliente: Regula tem portal próprio (saúde regulatória +
 * economia por centro); demais empresas (M5/Átomo) seguem no portal de
 * facilities. Mesma lógica do DashboardSwitcher.
 */
function PortalSwitcher() {
  const { ativa, loading } = useEmpresaAtiva();
  // Achado #45 (auditoria 05/07): mesmo flash do DashboardSwitcher acima.
  if (loading || !ativa) return <div className="p-8 text-muted-foreground">Carregando…</div>;
  if (ativa?.slug === "regula360" || ativa?.slug === "vitrine-reg") return <HomeDoClientePage />;
  return <PortalPage />;
}

// Deep-link deslogado (ex.: e-mail "Proposta ACEITA — crie a OS" aponta pra
// /orcamentos?criar_os=<id>): o redirect pro /login perdia o destino, e o Login
// navega pra "/" — o param morria. Guardamos o destino em sessionStorage antes
// de redirecionar e restauramos no primeiro render logado. Sem tocar no Login.
const POS_LOGIN_KEY = "pos-login-destino";

export function PrivateRoute({ children, clienteRedirect = true, soOperador = false }: { children: React.ReactNode; clienteRedirect?: boolean; soOperador?: boolean }) {
  const { user, bootResolvido } = useAuth(); // `bootResolvido`, NÃO `loading` — ver AuthContextValue
  // 🔒 ESPERAR A EMPRESA ATIVA CARREGAR (auditoria de Sistema, 14/07).
  //
  // A rota renderizava assim que o AUTH resolvia, sem esperar a EMPRESA: as telas
  // disparavam queries sem o header X-Empresa-Id e um F5 misturava M5 + Regula +
  // Átomo na MESMA lista, porque `get_my_empresas()` era FAIL-OPEN.
  // 🟥 ESTA PROSA ESTAVA VENCIDA — corrigida em 17/08/2026 com a função MEDIDA no banco
  // vivo (`pg_get_functiondef`). Hoje ela é FAIL-CLOSED: header válido → só a empresa
  // ativa; UM vínculo → a empresa dele; 2+ sem header → VAZIO, negação no log (#1351).
  // Esperar continua certo pelo motivo INVERSO: sem header a tela não mostra dado errado,
  // mostra NADA — e lista vazia parece dado. ⚠️ Comentário que descreve banco envelhece: MEÇA.
  const { loading: carregandoEmpresa, erro: erroEmpresa } = useEmpresaAtiva();
  if (!bootResolvido || (user && carregandoEmpresa)) return <CarregandoSessao />;
  // Resolveu e NÃO tem empresa ativa: dizer o que houve em vez de renderizar a
  // casca vazia (menu curto + telas presas). O gate é aqui porque é aqui que a
  // ausência de empresa ativa vira tela quebrada.
  if (user && erroEmpresa) {
    return <EmpresaIndisponivel erro={erroEmpresa} />;
  }
  if (!user) {
    try {
      const destino = window.location.pathname + window.location.search;
      // ALLOWLIST de deep-links reais: guardar qualquer query preservaria ruído
      // (?marca= do convite, ?code= do recovery PKCE) e geraria redirect inútil.
      const params = new URLSearchParams(window.location.search);
      if (params.has("criar_os") || params.has("abrir")) {
        sessionStorage.setItem(POS_LOGIN_KEY, destino);
      }
    } catch { /* storage indisponível — segue sem preservar */ }
    return <Navigate to="/login" replace />;
  }
  // Logado: se havia um destino guardado (login no meio do caminho), vai pra lá.
  // Deep-links da allowlist são rotas de OPERAÇÃO — cliente não é levado até lá
  // (a casca renderizaria vazia; RLS protege o dado, mas a viagem confunde).
  try {
    const destino = sessionStorage.getItem(POS_LOGIN_KEY);
    if (destino) {
      sessionStorage.removeItem(POS_LOGIN_KEY);
      if (user.role !== "cliente" && destino !== window.location.pathname + window.location.search) {
        return <Navigate to={destino} replace />;
      }
    }
  } catch { /* storage indisponível */ }
  // SEC-07: rotas de operação (ex.: Torre de Controle) não abrem pra cliente —
  // trava de papel na rota, não só esconder do menu.
  if (user.role === "cliente" && soOperador) {
    return <Navigate to="/portal" replace />;
  }
  if (user.role === "cliente" && clienteRedirect && window.location.pathname === "/") {
    return <Navigate to="/portal" replace />;
  }
  children = <><AvisoDuasEtapas />{children}</>; /* mock 11: a faixa "ative até DD/MM" (só Regula) */ return <PortaoDuasEtapas><Layout><AvisoListaCortada />{children}</Layout></PortaoDuasEtapas>;
}

export function App() {
  // As páginas SEM login moram em `rotas/paginaPublica` — são montadas ANTES
  // dos providers de sessão de propósito: quem abre `/p/<token>` não tem sessão.
  const publica = paginaPublicaDaUrl(typeof window !== "undefined" ? window.location.pathname : "");
  if (publica) return publica;
  return (
    <AuthProvider><BilheteDoPdf />
      <EmpresaProvider>
      <VerComoProvider>
      <RecoveryGate>
      <ChunkErrorBoundary><Suspense fallback={<PageFallback />}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route
          path="/"
          element={
            <PrivateRoute>
              <DashboardSwitcher />
            </PrivateRoute>
          }
        />
        <Route
          path="/precisa-de-voce"
          element={
            <PrivateRoute>
              <PrecisaDeVocePage />
            </PrivateRoute>
          }
        />
        <Route
          path="/clientes"
          element={
            <PrivateRoute>
              <ClientesPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/mapa-da-carteira"
          element={
            <PrivateRoute>
              <MapaCarteiraPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/pipeline"
          element={
            <PrivateRoute>
              <PipelinePage />
            </PrivateRoute>
          }
        /> <Route path="/prospeccao" element={<PrivateRoute><ProspeccaoPage /></PrivateRoute>} /> {/* a tela de Prospecção (mock aprovado 12/09) */}
        <Route
          path="/obras"
          element={
            <PrivateRoute>
              <ObrasSwitcher />
            </PrivateRoute>
          }
        />
        <Route
          path="/obras/localizacao"
          element={
            <PrivateRoute>
              <ObrasLocalizacaoPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/obras/:id"
          element={
            <PrivateRoute>
              <ObraDetalheSwitcher />
            </PrivateRoute>
          }
        />
        <Route
          path="/cronograma"
          element={
            <PrivateRoute>
              <CronogramaPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/comunicacao"
          element={
            <PrivateRoute>
              <ComunicacaoPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/whatsapp"
          element={
            <PrivateRoute>
              <WhatsappPage />
            </PrivateRoute>
          }
        />
        {/* compat: links antigos /agenda e /email caem no módulo unificado */}
        <Route path="/agenda" element={<Navigate to="/comunicacao" replace />} />
        <Route path="/email" element={<Navigate to="/comunicacao" replace />} />
        <Route
          path="/orcamentos"
          element={
            <PrivateRoute>
              <OrcamentosPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/contratos"
          element={
            <PrivateRoute>
              <ContratosPage />
            </PrivateRoute>
          }
        />
        {/* As 2 portas da Regula v2 (numa linha cada: este arquivo está na dívida do portão
            de tamanho). ⚰️ Onda 3 (18/08): o Painel (/painel-regula) MORREU — Carteira por
            cliente e o placar Boletos/Motor migraram pra Torre (components/torre/). */}
        <Route path="/portfolio" element={<PrivateRoute soOperador><PortfolioRegulaPage /></PrivateRoute>} />
        <Route path="/mesa-revisao" element={<PrivateRoute soOperador><MesaRevisaoRegulaPage /></PrivateRoute>} /> <Route path="/linkedin-regula" element={<PrivateRoute soOperador><LinkedInRegulaPage /></PrivateRoute>} /> <Route path="/a-confirmar" element={<PrivateRoute soOperador><DocumentosAConfirmarPage /></PrivateRoute>} /> {/* 28/10: o balde "a confirmar" */}
        <Route
          path="/documentos-regula"
          element={
            <PrivateRoute clienteRedirect={false}>
              <DocumentosRegulaPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/simulador-proposta"
          element={
            <PrivateRoute soOperador>
              <PropostaSimuladorPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/medicoes"
          element={
            <PrivateRoute>
              <MedicoesPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/saude-manutencao"
          element={
            <PrivateRoute>
              <SaudeManutencaoPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/ativos"
          element={
            <PrivateRoute>
              <AtivosPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/acesso"
          element={
            <PrivateRoute>
              <AcessoPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/colaboradores"
          element={
            <PrivateRoute>
              <ColaboradoresPage />
            </PrivateRoute>
          }
        />
        {/* A tela se chama PESSOAS desde o redesign, mas a rota nasceu
            /colaboradores — quem procurava /pessoas caía num 404 e quem tinha o
            link antigo nos favoritos precisava continuar chegando. As duas
            servem O MESMO componente: rótulo e endereço passam a combinar sem
            quebrar nada que já estava salvo. */}
        <Route
          path="/pessoas"
          element={
            <PrivateRoute>
              <ColaboradoresPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/portal"
          element={
            <PrivateRoute clienteRedirect={false}>
              <PortalSwitcher />
            </PrivateRoute>
          }
        />
        <Route path="/meus-chamados" element={<PrivateRoute clienteRedirect={false}><MeusChamadosPage /></PrivateRoute>} />
        <Route path="/portal-regula" element={<PrivateRoute clienteRedirect={false}><HomeDoClientePage /></PrivateRoute>} />
        <Route path="/portal-regula/:id" element={<PrivateRoute clienteRedirect={false}><PortalRegulaCentroPage /></PrivateRoute>} /> <Route path="/portal-regula/:id/manual" element={<PrivateRoute clienteRedirect={false}><ManualDoImovelPage /></PrivateRoute>} />
        {/* Rotas do PORTAL em uma linha cada — o portão cobra este arquivo (só encolhe); mesmo elemento, mesma guarda, mesma ordem. */}
        <Route path="/portal-economia" element={<PrivateRoute clienteRedirect={false}><PortalEconomiaPage /></PrivateRoute>} />
        <Route path="/portal-vencimentos" element={<PrivateRoute clienteRedirect={false}><PortalVencimentosPage /></PrivateRoute>} /><Route path="/portal-boletos" element={<PrivateRoute clienteRedirect={false}><PortalBoletosPage /></PrivateRoute>} /><Route path="/central-boletos" element={<PrivateRoute soOperador><CentralBoletosPage /></PrivateRoute>} />
        <Route path="/portal-o-que-mudou" element={<PrivateRoute clienteRedirect={false}><OQueMudouPage /></PrivateRoute>} />
        <Route path="/portal-comparar" element={<PrivateRoute clienteRedirect={false}><CompararUnidadesPage /></PrivateRoute>} /> <Route path="/portal-rateio" element={<PrivateRoute clienteRedirect={false}><PiramideDoRateioPage /></PrivateRoute>} /> <Route path="/portal-board" element={<PrivateRoute clienteRedirect={false}><PainelDoBoardPage /></PrivateRoute>} />
        <Route path="/portal-renovacoes" element={<PrivateRoute clienteRedirect={false}><RenovacoesPage /></PrivateRoute>} />
        <Route path="/equipe" element={<PrivateRoute clienteRedirect={false}><EquipePage /></PrivateRoute>} />
        <Route
          path="/fiscal"
          element={
            <PrivateRoute>
              <FiscalV2Page />
            </PrivateRoute>
          }
        />
        <Route
          path="/painel-dinheiro"
          element={
            <PrivateRoute>
              <PainelDinheiroSwitcher />
            </PrivateRoute>
          }
        />
        <Route
          path="/conciliacao"
          element={
            <PrivateRoute>
              <ConciliacaoSwitcher />
            </PrivateRoute>
          }
        />
        <Route
          path="/mensagens"
          element={
            <PrivateRoute>
              <MensagensPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/checklists"
          element={
            <PrivateRoute>
              <ChecklistsPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/rh"
          element={
            <PrivateRoute>
              <RHPage />
            </PrivateRoute>
          }
        />
        {/* "Estoque & Compras" MORREU (14/07 — auditoria de Recursos).
            As três tabelas (compras, compras_itens, movimentacoes_estoque) tinham ZERO
            linhas. Nunca receberam um único registro em toda a vida do sistema, e a tela
            ficava no balde "EM BREVE" do menu — que, depois de meses, não é promessa: é
            mentira educada. Tela vazia por tanto tempo não é "ainda não usaram" — é resposta.
            A compra de verdade já vira conta a pagar ou despesa do bolso, que é onde o
            dinheiro é registrado. Estoque era uma SEGUNDA contabilidade que ninguém alimentava.
            As TABELAS continuam no banco (não custam nada, e não se joga dado fora), e o
            desenho de como ressuscitar isso direito — alimentado pela NF que a Duda já lê,
            sem ninguém digitar — está em docs/ADR-004-estoque-compras.md.
            Quem tiver o link salvo cai na home, não numa tela em branco. */}
        <Route path="/estoque" element={<Navigate to="/" replace />} />
        {/* "Indicadores" MORREU (13/07). Era uma GAVETA: três perguntas de três donos
            diferentes empilhadas porque todas tinham gráfico. Rentabilidade por cliente
            mentia (só via conta a pagar: a Genetec aparecia com 100% de margem porque o
            custo saiu do bolso dos sócios). A Curva ABC foi pra Contas a Pagar e o Funil
            de Captação foi pro Comercial — cada um onde a pergunta é feita.
            Quem tinha o link salvo cai no Painel. */}
        <Route path="/bi" element={<Navigate to="/" replace />} />
        <Route
          path="/contas-receber"
          element={
            <PrivateRoute>
              <ContasReceberV2Page />
            </PrivateRoute>
          }
        />
        <Route
          path="/contas-pagar"
          element={
            <PrivateRoute>
              <ContasPagarV2Page />
            </PrivateRoute>
          }
        />
        <Route
          path="/documentos"
          element={
            <PrivateRoute>
              <MandaDocumentoPage />
            </PrivateRoute>
          }
        />
        {/* Achado #35 (auditoria 05/07): faltava soOperador nas rotas do Operador
            — cliente digitando a URL na mão não era redirecionado (o backend
            bloqueia os dados, mas a casca "Plataforma"/VORTEK renderizava). */}
        <Route
          path="/operador"
          element={
            <PrivateRoute soOperador>
              <OperadorPainelPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/operador/clientes"
          element={
            <PrivateRoute soOperador>
              <OperadorClientesPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/operador/auditorias"
          element={
            <PrivateRoute soOperador>
              <OperadorAuditoriasPage />
            </PrivateRoute>
          }
        />
        <Route path="/operador/seguranca" element={<PrivateRoute soOperador><OperadorSegurancaPage /></PrivateRoute>} />
        <Route path="/operador/telemetria" element={<PrivateRoute soOperador><OperadorTelemetriaPage /></PrivateRoute>} />
        <Route path="/operador/pareceres" element={<PrivateRoute soOperador><OperadorPareceresPage /></PrivateRoute>} />
        {/* A MESMA tela de /notificacoes, na camada PLATAFORMA (a régua usuarios-camada barra rota de empresa no menu do operador) */}
        <Route path="/operador/notificacoes" element={<PrivateRoute soOperador><NotificacoesPage /></PrivateRoute>} />
        <Route
          path="/operador/canais"
          element={
            <PrivateRoute soOperador>
              <OperadorCanaisPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/operador/base-comparativa"
          element={
            <PrivateRoute soOperador>
              <BaseComparativaPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/operador/conferencia-folha"
          element={
            <PrivateRoute soOperador>
              <OperadorConferenciaFolhaPage />
            </PrivateRoute>
          }
        />
        {/* Usuários DA PLATAFORMA (todas as empresas) — camada VORTEK, acessível
            SÓ pelo menu da plataforma. Era a mesma /usuarios do menu Pessoas da
            empresa: dentro do Átomo, o dono caía aqui sem sair da empresa, e a
            tela listava usuário de todos os tenants com a faixa jurando que a
            aba continuava no Átomo (27/07). Ver src/client/lib/operador.ts. */}
        <Route
          path="/operador/usuarios"
          element={
            <PrivateRoute soOperador>
              <UsuariosPlataformaPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/manual-de-bordo"
          element={
            <PrivateRoute soOperador>
              <ManualDeBordoPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/socios"
          element={
            <PrivateRoute>
              <SociosV2Page />
            </PrivateRoute>
          }
        />
        {/* Sócios v2: o Livro é página própria e vale em QUALQUER pele — a trava que este comentário prometia nunca existiu (medido 17/08). Motivo: tests/marca-da-regula-nao-herda-a-m5.test.ts */}
        <Route path="/socios/livro" element={<PrivateRoute><LivroSociosV2Page /></PrivateRoute>} />
        <Route path="/socios/semana" element={<PrivateRoute><SociosSemanaPage /></PrivateRoute>} /> {/* 25/09: o parecer da semana com as fontes (destino do link do WhatsApp) */}
        <Route
          path="/engenharia"
          element={
            <PrivateRoute>
              <EngenhariaPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/catalogo-servicos"
          element={
            <PrivateRoute>
              <CatalogoServicosPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/prestadores"
          element={
            <PrivateRoute>
              <PrestadoresPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/plano-acoes"
          element={
            <PrivateRoute>
              <PlanoAcoesPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/ledger"
          element={
            <PrivateRoute>
              <LedgerPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/vencimentos"
          element={
            <PrivateRoute>
              <VencimentosPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/fechamento"
          element={
            <PrivateRoute>
              <FechamentoV2Page />
            </PrivateRoute>
          }
        />
        <Route path="/razao" element={<PrivateRoute><RazaoV2Page /></PrivateRoute>} /> {/* F3: o Razão; sem item de menu até o dono decidir onde mora */} <Route path="/conciliacao-lista" element={<PrivateRoute><ConciliacaoListaPage /></PrivateRoute>} /> {/* endereço antigo da tela nova: segue vivo pros links que a Duda já mandou no zap */}
        <Route
          path="/folha"
          element={
            <PrivateRoute>
              <FolhaPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/tributario"
          element={
            <PrivateRoute>
              <TributarioPage />
            </PrivateRoute>
          }
        />
        {/* Usuários DA EMPRESA ATIVA (menu Pessoas) — camada empresa. Nunca vira
            VORTEK, nunca lista outro tenant. O painel da plataforma mora em
            /operador/usuarios. */}
        <Route
          path="/usuarios"
          element={
            <PrivateRoute>
              <UsuariosPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/bi-site"
          element={
            <PrivateRoute soOperador>
              <BISitePage />
            </PrivateRoute>
          }
        />
        <Route
          path="/sobre"
          element={
            <PrivateRoute soOperador>
              <SobrePage />
            </PrivateRoute>
          }
        />
        <Route path="/auditoria" element={<PrivateRoute><AuditoriaPage /></PrivateRoute>} /> <Route path="/api-integracoes" element={<PrivateRoute soOperador><ApiIntegracoesRegulaPage /></PrivateRoute>} /> <Route path="/minha-conta/duas-etapas" element={<PrivateRoute clienteRedirect={false}><DuasEtapasPage /></PrivateRoute>} /> {/* D12 (11/10): chaves e webhooks da API pública da Regula; só operador (o servidor exige admin) */}
        <Route path="/notificacoes" element={<PrivateRoute><NotificacoesPage /></PrivateRoute>} /> {/* push: todo papel; o servidor filtra os eventos do papel */}
        <Route
          path="/centros"
          element={
            <PrivateRoute soOperador>
              <CentrosPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/centros/:id"
          element={
            <PrivateRoute soOperador>
              <CentroDetalhePage />
            </PrivateRoute>
          }
        />
        <Route
          path="/fontes-auditoria"
          element={
            <PrivateRoute soOperador>
              <FontesAuditoriaPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/requisitos-conformidade"
          element={
            <PrivateRoute soOperador>
              <RequisitosConformidadePage />
            </PrivateRoute>
          }
        />
        <Route
          path="/achados"
          element={
            <PrivateRoute soOperador>
              <AchadosPage />
            </PrivateRoute>
          }
        /> <Route path="/risco-interdicao" element={<PrivateRoute soOperador><RiscoDeInterdicaoPage /></PrivateRoute>} /> <Route path="/radar-reforma" element={<PrivateRoute soOperador><RadarDaReformaPage /></PrivateRoute>} /> <Route path="/autos-infracao" element={<PrivateRoute soOperador><AutosDeInfracaoPage /></PrivateRoute>} /> <Route path="/auditoria-do-contrato" element={<PrivateRoute soOperador><AuditoriaDoContratoPage /></PrivateRoute>} /> <Route path="/renovacoes-calendario" element={<PrivateRoute clienteRedirect={false}><CalendarioDeRenovacoesPage /></PrivateRoute>} /> <Route path="/renovar-ou-nao" element={<PrivateRoute clienteRedirect={false}><RenovarOuNaoPage /></PrivateRoute>} /> <Route path="/roteiros-renovacao" element={<PrivateRoute soOperador><EditorDeRoteirosPage /></PrivateRoute>} /> <Route path="/carteira-seguradora" element={<PrivateRoute clienteRedirect={false}><CarteiraSeguradoraRegulaPage /></PrivateRoute>} /> <Route path="/esg-regula" element={<PrivateRoute clienteRedirect={false}><EsgRegulaPage /></PrivateRoute>} /> {/* carteira para a seguradora (mock 03, 14/10) · D5 (mock 13, 10/10) · D8 (mock 14, 11/10): abaixo de Risco de interdição e D10 (mock 16, 11/10): abaixo de Achados no menu Auditoria */}
        <Route
          path="/mapa-cobertura"
          element={
            <PrivateRoute soOperador>
              <MapaCoberturaPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/faturamento-regula"
          element={
            <PrivateRoute soOperador>
              <FaturamentoRegulaPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/onboarding-regula"
          element={
            <PrivateRoute soOperador>
              <OnboardingRegulaPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/biblioteca"
          element={
            <PrivateRoute soOperador>
              <BibliotecaPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/repositorio"
          element={
            <PrivateRoute>
              <Repositorio />
            </PrivateRoute>
          }
        />
        <Route
          path="/custo-por-cliente"
          element={
            <PrivateRoute>
              <CustoPorCliente />
            </PrivateRoute>
          }
        />
        {/* Aliases/redirects de URLs antigas — não quebra bookmarks */}
        <Route path="/financeiro" element={<Navigate to="/ledger" replace />} />
        <Route path="/caixa" element={<Navigate to="/ledger" replace />} />
        <Route path="/dre" element={<Navigate to="/fechamento" replace />} />
        {/* Achado #44 (auditoria 05/07): esta rota /folha era código morto —
            já existe uma rota real /folha (linha ~566, FolhaPage) declarada
            ANTES desta; o react-router usa a primeira que casar, então este
            redirect pra /rh nunca era alcançado. Nenhum link do app dependia
            dele (grep confirmado). Removido pra não confundir quem ler o
            arquivo achando que /folha redireciona pro RH. */}

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </Suspense></ChunkErrorBoundary>
      </RecoveryGate>
      </VerComoProvider>
      </EmpresaProvider>
    </AuthProvider>
  );
}
